import React, { useContext, useEffect, useMemo, useRef } from 'react';

interface IScrollListener {
  (e: Event): void;
}

/**
 * Allow children too subscribe to changes to
 */
export interface IScrollContext {
  scrollParent: HTMLElement | null; // should this be a ref to prevent extra re-renders or will it be the same either way?
  addScrollListener: (id: string, listener: IScrollListener) => void;
  removeScrollListener: (id: string) => void;
}

export const ScrollContext = React.createContext<IScrollContext | undefined>(undefined);

export interface IScrollProviderProps {
  scrollParent: HTMLElement | null;
  children: React.ReactNode;
}

export function ScrollProvider({ children, scrollParent }: IScrollProviderProps) {
  const handlers = useMemo(() => new Map<string, Function>(), []);
  const ticking = useRef(false);
  const value: IScrollContext = useMemo(
    () => ({
      addScrollListener: (id, listener) => {
        handlers.set(id, listener);
      },
      scrollParent,
      removeScrollListener: id => handlers.delete(id),
    }),
    [handlers, scrollParent]
  );

  useEffect(() => {
    if (!scrollParent) {
      return;
    }
    // this wasn't used / tested yet, so be aware it might need some work if you need to use it.
    const scrollHandler = (_e: Event) => {
      if (!ticking.current) {
        requestAnimationFrame((_interval: number) => {
          // e.target.
          // TODO pass the bounding box and scroll position of the
          handlers.forEach(handler => handler());
          ticking.current = true;
        });

        ticking.current = true;
      }
    };
    scrollParent.addEventListener('scroll', scrollHandler);

    return () => scrollParent.removeEventListener('scroll', scrollHandler);
  }, [handlers, scrollParent]);

  return <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>;
}

/**
 * A reusable hook to make `ScrollContext` easier to use for most cases,
 * and enforce that it exists.
 * @param propsTabIndex tab index from props
 */
export function useScroll() {
  const context = useContext(ScrollContext);

  if (!context) {
    throw new Error('useScroll must be used within a ScrollProvider');
  }
  return context;
}

const safeEmptyScrollContext = {
  scrollParent: null, // should this be a ref to prevent extra re-renders or will it be the same either way?
  addScrollListener: (_id: string, _listener: IScrollListener) => {
    throw new Error('useScroll.addScrollListener must be used within a ScrollProvider');
  },
  removeScrollListener: (_id: string) => {
    throw new Error('useScroll.addScrollListener must be used within a ScrollProvider');
  },
};

/**
 * Use this only for things that won't always be wrapped in a scroll context.
 * @returns context if it's defined or a fallback value
 */
export function useScrollSafe() {
  const context = useContext(ScrollContext);

  if (!context) {
    // return some empty stuff if we don't
    return safeEmptyScrollContext;
  }

  return context;
}
